import React, { useState, useEffect, useCallback } from 'react';
import { useTheme } from '../context/ThemeContext';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

const CATEGORIES = [
  'Disease Surveillance',
  'Public Health',
  'Vaccination',
  'Health Equity',
  'Epidemiology',
  'Data Analytics',
  'Policy & Compliance',
  'Technology',
  'Case Studies',
];

const CATEGORY_COLORS = {
  'Disease Surveillance': '#ef4444',
  'Public Health': '#3b82f6',
  'Vaccination': '#22c55e',
  'Health Equity': '#a855f7',
  'Epidemiology': '#f97316',
  'Data Analytics': '#0ea5e9',
  'Policy & Compliance': '#64748b',
  'Technology': '#8b5cf6',
  'Case Studies': '#eab308',
};

const authHeaders = () => ({
  'Authorization': 'Bearer ' + localStorage.getItem('token'),
  'Content-Type': 'application/json',
});

const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

const EMPTY_FORM = {
  title: '', category: '', excerpt: '', content: '', tags: '', status: 'draft',
};

/* ─── Loading Dots ─── */
const LoadingDots = () => {
  const [dots, setDots] = useState('');
  useEffect(() => {
    const t = setInterval(() => setDots(d => d.length >= 3 ? '' : d + '.'), 400);
    return () => clearInterval(t);
  }, []);
  return <span>{dots}</span>;
};

/* ─── MAIN COMPONENT ─── */
const BlogAdmin = () => {
  const { isDark } = useTheme();

  // Tabs: 'posts' | 'editor' | 'ai-topics'
  const [tab, setTab] = useState('posts');

  /* ── Posts tab state ── */
  const [posts, setPosts] = useState([]);
  const [postsLoading, setPostsLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  /* ── Editor tab state ── */
  const [form, setForm] = useState(EMPTY_FORM);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');
  const [aiGenerating, setAiGenerating] = useState(false);
  const [aiTopicsModal, setAiTopicsModal] = useState(false);
  const [aiTopics, setAiTopics] = useState([]);
  const [aiTopicsLoading, setAiTopicsLoading] = useState(false);
  const [topicCategory, setTopicCategory] = useState('');

  /* ── AI Topics tab state ── */
  const [topicsTabCat, setTopicsTabCat] = useState('');
  const [topicsTabData, setTopicsTabData] = useState([]);
  const [topicsTabLoading, setTopicsTabLoading] = useState(false);

  const c = {
    pageBg:        isDark ? '#020617' : '#f8fafc',
    cardBg:        isDark ? '#0f172a' : '#ffffff',
    cardBg2:       isDark ? '#0a0f1e' : '#f8fafc',
    border:        isDark ? '#1e293b' : '#e2e8f0',
    textPrimary:   isDark ? '#f1f5f9' : '#0f172a',
    textSecondary: isDark ? '#94a3b8' : '#64748b',
    inputBg:       isDark ? '#0f172a' : '#ffffff',
    inputBorder:   isDark ? '#1e293b' : '#d1d5db',
    accent:        '#00d4aa',
    rowHover:      isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
    tableHead:     isDark ? '#0a0f1e' : '#f8fafc',
    modalOverlay:  'rgba(0,0,0,0.6)',
    modalBg:       isDark ? '#0f172a' : '#ffffff',
  };

  const inputStyle = {
    width: '100%', padding: '10px 14px', borderRadius: 10,
    border: `1px solid ${c.inputBorder}`, background: c.inputBg,
    color: c.textPrimary, fontSize: 14, outline: 'none',
    fontFamily: "'Plus Jakarta Sans', sans-serif", transition: 'border-color 0.2s',
    boxSizing: 'border-box',
  };
  const labelStyle = { display: 'block', fontSize: 13, fontWeight: 600, color: c.textSecondary, marginBottom: 6 };

  /* ── Fetch posts ── */
  const fetchPosts = useCallback(async () => {
    setPostsLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 20 });
      if (statusFilter !== 'all') params.set('status', statusFilter);
      const res = await fetch(`${API_BASE}/blog?${params}`, { headers: authHeaders() });
      const data = await res.json();
      setPosts(data.posts || data.data || []);
      setTotalPages(data.totalPages || data.pagination?.totalPages || 1);
    } catch { setPosts([]); }
    setPostsLoading(false);
  }, [page, statusFilter]);

  useEffect(() => { if (tab === 'posts') fetchPosts(); }, [tab, fetchPosts]);

  /* ── Delete post ── */
  const handleDelete = async (id) => {
    setActionLoading(id + '_del');
    try {
      await fetch(`${API_BASE}/blog/${id}`, { method: 'DELETE', headers: authHeaders() });
      setDeleteConfirm(null);
      fetchPosts();
    } catch { /* noop */ }
    setActionLoading(null);
  };

  /* ── Toggle publish ── */
  const handleTogglePublish = async (post) => {
    const newStatus = post.status === 'published' ? 'draft' : 'published';
    setActionLoading(post._id + '_pub');
    try {
      await fetch(`${API_BASE}/blog/${post._id}`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({ status: newStatus }),
      });
      fetchPosts();
    } catch { /* noop */ }
    setActionLoading(null);
  };

  /* ── Open editor for edit ── */
  const openEdit = (post) => {
    setEditId(post._id);
    setForm({
      title: post.title || '',
      category: post.category || '',
      excerpt: post.excerpt || '',
      content: post.content || '',
      tags: Array.isArray(post.tags) ? post.tags.join(', ') : (post.tags || ''),
      status: post.status || 'draft',
    });
    setSaveMsg('');
    setTab('editor');
  };

  const openCreate = () => {
    setEditId(null);
    setForm(EMPTY_FORM);
    setSaveMsg('');
    setTab('editor');
  };

  /* ── Save post ── */
  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { setSaveMsg('Title is required.'); return; }
    if (!form.category) { setSaveMsg('Category is required.'); return; }
    setSaving(true);
    setSaveMsg('');
    try {
      const payload = {
        ...form,
        tags: form.tags.split(',').map(t => t.trim()).filter(Boolean),
      };
      const url = editId ? `${API_BASE}/blog/${editId}` : `${API_BASE}/blog`;
      const method = editId ? 'PUT' : 'POST';
      const res = await fetch(url, { method, headers: authHeaders(), body: JSON.stringify(payload) });
      if (!res.ok) throw new Error('Failed');
      setSaveMsg(editId ? 'Post updated successfully!' : 'Post created successfully!');
      if (!editId) { setForm(EMPTY_FORM); }
    } catch {
      setSaveMsg('Failed to save. Please try again.');
    }
    setSaving(false);
  };

  /* ── AI: Suggest Topics (modal) ── */
  const handleSuggestTopics = async () => {
    setAiTopicsModal(true);
    setAiTopics([]);
    setAiTopicsLoading(true);
    try {
      const res = await fetch(`${API_BASE}/blog/ai/suggest-topics`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ category: topicCategory || undefined }),
      });
      const data = await res.json();
      setAiTopics(data.topics || data.suggestions || []);
    } catch { setAiTopics([]); }
    setAiTopicsLoading(false);
  };

  /* ── AI: Generate full post ── */
  const handleGeneratePost = async () => {
    if (!form.title) { setSaveMsg('Enter a title first.'); return; }
    setAiGenerating(true);
    setSaveMsg('');
    try {
      const res = await fetch(`${API_BASE}/blog/ai/generate`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ title: form.title, category: form.category }),
      });
      const data = await res.json();
      const generated = data.post || data;
      setForm(prev => ({
        ...prev,
        title: generated.title || prev.title,
        excerpt: generated.excerpt || prev.excerpt,
        content: generated.content || prev.content,
        tags: Array.isArray(generated.tags) ? generated.tags.join(', ') : (generated.tags || prev.tags),
        category: generated.category || prev.category,
      }));
      setSaveMsg('AI post generated! Review and edit before saving.');
    } catch {
      setSaveMsg('AI generation failed. Try again.');
    }
    setAiGenerating(false);
  };

  /* ── AI Topics tab: fetch ── */
  const handleFetchTopicsTab = async () => {
    setTopicsTabLoading(true);
    setTopicsTabData([]);
    try {
      const res = await fetch(`${API_BASE}/blog/ai/suggest-topics`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ category: topicsTabCat || undefined }),
      });
      const data = await res.json();
      setTopicsTabData(data.topics || data.suggestions || []);
    } catch { setTopicsTabData([]); }
    setTopicsTabLoading(false);
  };

  /* ── Select topic from AI Topics tab ── */
  const selectTopicFromTab = (topic) => {
    setEditId(null);
    setForm({
      ...EMPTY_FORM,
      title: topic.title || '',
      category: topic.category || '',
      excerpt: topic.description || topic.excerpt || '',
    });
    setSaveMsg('');
    setTab('editor');
  };

  /* ══════════════════════════════════
     RENDER
  ══════════════════════════════════ */
  return (
    <div style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", color: c.textPrimary }}>
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        .ba-tab-btn { padding: 10px 20px; border-radius: 10px; border: 1px solid transparent; font-size: 14px; font-weight: 600; cursor: pointer; font-family: 'Plus Jakarta Sans', sans-serif; transition: all 0.2s; }
        .ba-tr:hover td { background: ${c.rowHover}; }
        .ba-input:focus { border-color: #00d4aa !important; }
        .ba-btn { display: inline-flex; align-items: center; gap: 6px; padding: 8px 16px; border-radius: 8px; font-size: 13px; font-weight: 600; cursor: pointer; border: none; font-family: 'Plus Jakarta Sans', sans-serif; transition: all 0.2s; }
        .ba-btn:hover { opacity: 0.88; }
        .ba-btn:disabled { opacity: 0.5; cursor: not-allowed; }
      `}</style>

      {/* ─── Page Header ─── */}
      <div style={{ marginBottom: 28 }}>
        <h1 className="page-title" style={{ fontSize: 26, fontWeight: 800, color: c.textPrimary, margin: '0 0 4px' }}>Blog Management</h1>
        <p style={{ color: c.textSecondary, fontSize: 14, margin: 0 }}>Create and manage blog posts with AI-assisted content generation.</p>
      </div>

      {/* ─── Tabs ─── */}
      <div className="blog-tabs" style={{ display: 'flex', gap: 8, marginBottom: 24 }}>
        {[
          { key: 'posts', label: 'All Posts' },
          { key: 'editor', label: editId ? 'Edit Post' : 'Create Post' },
          { key: 'ai-topics', label: 'AI Topics' },
        ].map(t => (
          <button
            key={t.key}
            className="ba-tab-btn"
            onClick={() => setTab(t.key)}
            style={{
              background: tab === t.key ? c.accent : c.cardBg,
              color: tab === t.key ? '#020617' : c.textSecondary,
              border: `1px solid ${tab === t.key ? c.accent : c.border}`,
            }}
          >
            {t.label}
          </button>
        ))}
        <button
          className="ba-btn"
          style={{ marginLeft: 'auto', background: c.accent, color: '#020617' }}
          onClick={openCreate}
        >
          + New Post
        </button>
      </div>

      {/* ══════════════════════════════
         TAB 1: ALL POSTS
      ══════════════════════════════ */}
      {tab === 'posts' && (
        <div style={{ background: c.cardBg, border: `1px solid ${c.border}`, borderRadius: 16, overflow: 'hidden' }}>
          {/* Toolbar */}
          <div style={{ padding: '16px 20px', borderBottom: `1px solid ${c.border}`, display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: c.textSecondary }}>Filter:</span>
            {['all', 'published', 'draft'].map(s => (
              <button
                key={s}
                onClick={() => { setStatusFilter(s); setPage(1); }}
                style={{
                  padding: '6px 14px', borderRadius: 8, border: `1px solid ${statusFilter === s ? c.accent : c.border}`,
                  background: statusFilter === s ? `${c.accent}18` : 'transparent',
                  color: statusFilter === s ? c.accent : c.textSecondary,
                  fontWeight: 600, fontSize: 12, cursor: 'pointer',
                  fontFamily: "'Plus Jakarta Sans', sans-serif", textTransform: 'capitalize',
                }}
              >
                {s.charAt(0).toUpperCase() + s.slice(1)}
              </button>
            ))}
          </div>

          {/* Table */}
          {postsLoading ? (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 60 }}>
              <div style={{ width: 32, height: 32, border: '3px solid #1e293b', borderTopColor: '#00d4aa', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
            </div>
          ) : posts.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '60px 24px' }}>
              <div style={{ fontSize: 40, marginBottom: 12 }}>📄</div>
              <p style={{ color: c.textSecondary, fontSize: 15 }}>No blog posts found.</p>
              <button className="ba-btn" onClick={openCreate} style={{ marginTop: 16, background: c.accent, color: '#020617' }}>
                + Create Your First Post
              </button>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: c.tableHead }}>
                    {['Title', 'Category', 'Status', 'Author', 'Created', 'Views', 'Actions'].map(h => (
                      <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: c.textSecondary, borderBottom: `1px solid ${c.border}`, whiteSpace: 'nowrap' }}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {posts.map(post => (
                    <tr key={post._id} className="ba-tr">
                      <td style={{ padding: '14px 16px', borderBottom: `1px solid ${c.border}` }}>
                        <div style={{ fontWeight: 600, color: c.textPrimary, fontSize: 14, maxWidth: 260 }} title={post.title}>
                          {post.title?.length > 50 ? post.title.slice(0, 50) + '…' : post.title}
                        </div>
                        {post.excerpt && (
                          <div style={{ fontSize: 12, color: c.textSecondary, marginTop: 2, maxWidth: 260 }}>
                            {post.excerpt?.length > 60 ? post.excerpt.slice(0, 60) + '…' : post.excerpt}
                          </div>
                        )}
                      </td>
                      <td style={{ padding: '14px 16px', borderBottom: `1px solid ${c.border}` }}>
                        {post.category ? (
                          <span style={{
                            padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700,
                            background: `${CATEGORY_COLORS[post.category] || '#64748b'}18`,
                            color: CATEGORY_COLORS[post.category] || '#64748b',
                            whiteSpace: 'nowrap',
                          }}>
                            {post.category}
                          </span>
                        ) : <span style={{ color: c.textSecondary, fontSize: 13 }}>—</span>}
                      </td>
                      <td style={{ padding: '14px 16px', borderBottom: `1px solid ${c.border}` }}>
                        <span style={{
                          padding: '4px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700,
                          background: post.status === 'published' ? 'rgba(34,197,94,0.12)' : 'rgba(100,116,139,0.12)',
                          color: post.status === 'published' ? '#22c55e' : '#64748b',
                        }}>
                          {post.status === 'published' ? 'Published' : 'Draft'}
                        </span>
                      </td>
                      <td style={{ padding: '14px 16px', borderBottom: `1px solid ${c.border}`, fontSize: 13, color: c.textSecondary, whiteSpace: 'nowrap' }}>
                        {post.author?.firstName} {post.author?.lastName}
                      </td>
                      <td style={{ padding: '14px 16px', borderBottom: `1px solid ${c.border}`, fontSize: 13, color: c.textSecondary, whiteSpace: 'nowrap' }}>
                        {formatDate(post.createdAt)}
                      </td>
                      <td style={{ padding: '14px 16px', borderBottom: `1px solid ${c.border}`, fontSize: 13, color: c.textSecondary }}>
                        {post.views ?? 0}
                      </td>
                      <td style={{ padding: '14px 16px', borderBottom: `1px solid ${c.border}` }}>
                        <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'nowrap' }}>
                          <button
                            className="ba-btn"
                            onClick={() => openEdit(post)}
                            style={{ background: isDark ? '#1e293b' : '#f1f5f9', color: c.textPrimary, padding: '6px 12px', fontSize: 12 }}
                          >
                            Edit
                          </button>
                          <button
                            className="ba-btn"
                            disabled={actionLoading === post._id + '_pub'}
                            onClick={() => handleTogglePublish(post)}
                            style={{
                              background: post.status === 'published' ? 'rgba(100,116,139,0.12)' : 'rgba(34,197,94,0.12)',
                              color: post.status === 'published' ? '#64748b' : '#22c55e',
                              padding: '6px 12px', fontSize: 12,
                            }}
                          >
                            {actionLoading === post._id + '_pub'
                              ? '...'
                              : post.status === 'published' ? 'Unpublish' : 'Publish'
                            }
                          </button>
                          <button
                            className="ba-btn"
                            onClick={() => setDeleteConfirm(post._id)}
                            style={{ background: 'rgba(239,68,68,0.1)', color: '#ef4444', padding: '6px 12px', fontSize: 12 }}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div style={{ padding: '16px 20px', display: 'flex', gap: 8, justifyContent: 'flex-end', borderTop: `1px solid ${c.border}` }}>
              <button className="ba-btn" disabled={page === 1} onClick={() => setPage(p => p - 1)} style={{ background: c.cardBg, color: c.textPrimary, border: `1px solid ${c.border}` }}>
                ← Prev
              </button>
              <span style={{ padding: '8px 14px', fontSize: 13, color: c.textSecondary }}>Page {page} of {totalPages}</span>
              <button className="ba-btn" disabled={page === totalPages} onClick={() => setPage(p => p + 1)} style={{ background: c.cardBg, color: c.textPrimary, border: `1px solid ${c.border}` }}>
                Next →
              </button>
            </div>
          )}
        </div>
      )}

      {/* ══════════════════════════════
         TAB 2: CREATE / EDIT POST
      ══════════════════════════════ */}
      {tab === 'editor' && (
        <div className="blog-editor-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 340px', gap: 24, alignItems: 'start' }}>
          {/* Main form */}
          <form onSubmit={handleSave}>
            <div style={{ background: c.cardBg, border: `1px solid ${c.border}`, borderRadius: 16, padding: 28, display: 'flex', flexDirection: 'column', gap: 20 }}>
              <div>
                <h2 style={{ fontSize: 18, fontWeight: 700, color: c.textPrimary, margin: '0 0 4px' }}>
                  {editId ? 'Edit Post' : 'Create New Post'}
                </h2>
                <p style={{ color: c.textSecondary, fontSize: 13, margin: 0 }}>Fill in the fields below. HTML is supported in the content area.</p>
              </div>

              {/* Title */}
              <div>
                <label style={labelStyle}>Title *</label>
                <input
                  className="ba-input"
                  type="text"
                  value={form.title}
                  onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                  placeholder="Enter post title..."
                  style={inputStyle}
                  onFocus={e => e.target.style.borderColor = '#00d4aa'}
                  onBlur={e => e.target.style.borderColor = c.inputBorder}
                />
              </div>

              {/* Category */}
              <div>
                <label style={labelStyle}>Category *</label>
                <select
                  value={form.category}
                  onChange={e => setForm(f => ({ ...f, category: e.target.value }))}
                  style={{ ...inputStyle, cursor: 'pointer' }}
                  onFocus={e => e.target.style.borderColor = '#00d4aa'}
                  onBlur={e => e.target.style.borderColor = c.inputBorder}
                >
                  <option value="">Select a category</option>
                  {CATEGORIES.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>

              {/* Excerpt */}
              <div>
                <label style={labelStyle}>Excerpt</label>
                <textarea
                  value={form.excerpt}
                  onChange={e => setForm(f => ({ ...f, excerpt: e.target.value }))}
                  placeholder="Brief summary of the post (2-3 sentences)..."
                  rows={3}
                  style={{ ...inputStyle, resize: 'vertical' }}
                  onFocus={e => e.target.style.borderColor = '#00d4aa'}
                  onBlur={e => e.target.style.borderColor = c.inputBorder}
                />
              </div>

              {/* Content */}
              <div>
                <label style={labelStyle}>
                  Content
                  <span style={{ fontWeight: 400, marginLeft: 6, color: c.textSecondary }}>(HTML is supported)</span>
                </label>
                <textarea
                  value={form.content}
                  onChange={e => setForm(f => ({ ...f, content: e.target.value }))}
                  placeholder="<h2>Introduction</h2><p>Your content here...</p>"
                  rows={18}
                  style={{ ...inputStyle, resize: 'vertical', fontFamily: 'monospace', fontSize: 13 }}
                  onFocus={e => e.target.style.borderColor = '#00d4aa'}
                  onBlur={e => e.target.style.borderColor = c.inputBorder}
                />
              </div>

              {/* Tags */}
              <div>
                <label style={labelStyle}>Tags <span style={{ fontWeight: 400 }}>(comma-separated)</span></label>
                <input
                  type="text"
                  value={form.tags}
                  onChange={e => setForm(f => ({ ...f, tags: e.target.value }))}
                  placeholder="epidemiology, disease, surveillance, AI..."
                  style={inputStyle}
                  onFocus={e => e.target.style.borderColor = '#00d4aa'}
                  onBlur={e => e.target.style.borderColor = c.inputBorder}
                />
              </div>

              {/* Status */}
              <div>
                <label style={labelStyle}>Status</label>
                <div style={{ display: 'flex', gap: 20 }}>
                  {['draft', 'published'].map(s => (
                    <label key={s} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 14, fontWeight: 500, color: c.textPrimary }}>
                      <input
                        type="radio"
                        name="status"
                        value={s}
                        checked={form.status === s}
                        onChange={() => setForm(f => ({ ...f, status: s }))}
                        style={{ accentColor: '#00d4aa', width: 16, height: 16 }}
                      />
                      {s === 'draft' ? 'Save as Draft' : 'Publish Now'}
                    </label>
                  ))}
                </div>
              </div>

              {/* Save message */}
              {saveMsg && (
                <div style={{
                  padding: '12px 16px', borderRadius: 10, fontSize: 13, fontWeight: 500,
                  background: saveMsg.includes('success') ? 'rgba(34,197,94,0.1)' : saveMsg.includes('AI post generated') ? 'rgba(0,212,170,0.1)' : 'rgba(239,68,68,0.1)',
                  color: saveMsg.includes('success') ? '#22c55e' : saveMsg.includes('AI post generated') ? '#00d4aa' : '#ef4444',
                  border: `1px solid ${saveMsg.includes('success') ? 'rgba(34,197,94,0.2)' : saveMsg.includes('AI post generated') ? 'rgba(0,212,170,0.2)' : 'rgba(239,68,68,0.2)'}`,
                }}>
                  {saveMsg}
                </div>
              )}

              {/* Submit */}
              <div style={{ display: 'flex', gap: 10 }}>
                <button
                  type="submit"
                  disabled={saving}
                  className="ba-btn"
                  style={{ background: c.accent, color: '#020617', padding: '11px 28px', fontSize: 14 }}
                >
                  {saving ? 'Saving...' : editId ? 'Update Post' : 'Create Post'}
                </button>
                <button
                  type="button"
                  className="ba-btn"
                  onClick={() => { setEditId(null); setForm(EMPTY_FORM); setSaveMsg(''); }}
                  style={{ background: 'transparent', color: c.textSecondary, border: `1px solid ${c.border}`, padding: '11px 20px', fontSize: 14 }}
                >
                  Clear
                </button>
              </div>
            </div>
          </form>

          {/* AI Sidebar */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {/* Suggest Topics */}
            <div style={{ background: c.cardBg, border: `1px solid ${c.border}`, borderRadius: 16, padding: 22 }}>
              <h3 style={{ fontSize: 15, fontWeight: 700, color: c.textPrimary, margin: '0 0 8px' }}>
                AI Topic Suggestions
              </h3>
              <p style={{ fontSize: 12, color: c.textSecondary, margin: '0 0 16px', lineHeight: 1.6 }}>
                Get AI-generated topic ideas to spark your next post.
              </p>
              <select
                value={topicCategory}
                onChange={e => setTopicCategory(e.target.value)}
                style={{ ...inputStyle, marginBottom: 10 }}
              >
                <option value="">All Categories</option>
                {CATEGORIES.map(cat => <option key={cat} value={cat}>{cat}</option>)}
              </select>
              <button
                className="ba-btn"
                onClick={handleSuggestTopics}
                style={{ width: '100%', justifyContent: 'center', background: isDark ? '#1e293b' : '#f1f5f9', color: c.textPrimary, border: `1px solid ${c.border}` }}
              >
                Suggest Topics
              </button>
            </div>

            {/* Generate Full Post */}
            <div style={{ background: c.cardBg, border: `1px solid ${c.border}`, borderRadius: 16, padding: 22 }}>
              <h3 style={{ fontSize: 15, fontWeight: 700, color: c.textPrimary, margin: '0 0 8px' }}>
                Generate Full Post
              </h3>
              <p style={{ fontSize: 12, color: c.textSecondary, margin: '0 0 16px', lineHeight: 1.6 }}>
                Fill in the title (and optionally category) above, then let AI write the full post for you.
              </p>
              {aiGenerating ? (
                <div style={{ textAlign: 'center', padding: '16px 0' }}>
                  <div style={{ width: 28, height: 28, border: '3px solid #1e293b', borderTopColor: '#00d4aa', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 10px' }} />
                  <p style={{ color: c.accent, fontSize: 13, fontWeight: 600 }}>
                    AI is writing your post<LoadingDots />
                  </p>
                </div>
              ) : (
                <button
                  className="ba-btn"
                  onClick={handleGeneratePost}
                  disabled={!form.title}
                  style={{
                    width: '100%', justifyContent: 'center',
                    background: form.title ? 'linear-gradient(135deg, #00d4aa, #00b894)' : (isDark ? '#1e293b' : '#f1f5f9'),
                    color: form.title ? '#020617' : c.textSecondary,
                  }}
                >
                  ✦ Generate Full Post
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ══════════════════════════════
         TAB 3: AI TOPICS
      ══════════════════════════════ */}
      {tab === 'ai-topics' && (
        <div>
          {/* Controls */}
          <div style={{ background: c.cardBg, border: `1px solid ${c.border}`, borderRadius: 16, padding: 24, marginBottom: 20 }}>
            <h2 style={{ fontSize: 17, fontWeight: 700, color: c.textPrimary, margin: '0 0 16px' }}>AI-Powered Topic Discovery</h2>
            <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
              <div style={{ flex: '1 1 220px' }}>
                <label style={labelStyle}>Filter by Category</label>
                <select
                  value={topicsTabCat}
                  onChange={e => setTopicsTabCat(e.target.value)}
                  style={inputStyle}
                >
                  <option value="">All Categories</option>
                  {CATEGORIES.map(cat => <option key={cat} value={cat}>{cat}</option>)}
                </select>
              </div>
              <button
                className="ba-btn"
                onClick={handleFetchTopicsTab}
                disabled={topicsTabLoading}
                style={{ background: c.accent, color: '#020617', padding: '10px 24px', fontSize: 14, flexShrink: 0 }}
              >
                {topicsTabLoading ? 'Generating...' : '✦ Suggest Topics'}
              </button>
            </div>
          </div>

          {/* Loading */}
          {topicsTabLoading && (
            <div style={{ textAlign: 'center', padding: '60px 0' }}>
              <div style={{ width: 36, height: 36, border: '3px solid #1e293b', borderTopColor: '#00d4aa', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 16px' }} />
              <p style={{ color: c.accent, fontSize: 14, fontWeight: 600 }}>Generating topic ideas<LoadingDots /></p>
            </div>
          )}

          {/* Topics grid */}
          {!topicsTabLoading && topicsTabData.length > 0 && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 16 }}>
              {topicsTabData.map((topic, i) => {
                const catColor = CATEGORY_COLORS[topic.category] || '#00d4aa';
                return (
                  <div
                    key={i}
                    style={{
                      background: c.cardBg, border: `1px solid ${c.border}`,
                      borderRadius: 14, padding: 20,
                      display: 'flex', flexDirection: 'column', gap: 10,
                      transition: 'all 0.2s', cursor: 'pointer',
                    }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = c.accent; e.currentTarget.style.transform = 'translateY(-2px)'; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = c.border; e.currentTarget.style.transform = 'none'; }}
                  >
                    {topic.category && (
                      <span style={{
                        display: 'inline-block', padding: '3px 10px', borderRadius: 20,
                        fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px',
                        background: `${catColor}18`, color: catColor, alignSelf: 'flex-start',
                      }}>
                        {topic.category}
                      </span>
                    )}
                    <h3 style={{ fontSize: 15, fontWeight: 700, color: c.textPrimary, margin: 0, lineHeight: 1.4 }}>
                      {topic.title}
                    </h3>
                    {(topic.description || topic.excerpt) && (
                      <p style={{ fontSize: 13, color: c.textSecondary, margin: 0, lineHeight: 1.6 }}>
                        {topic.description || topic.excerpt}
                      </p>
                    )}
                    <button
                      className="ba-btn"
                      onClick={() => selectTopicFromTab(topic)}
                      style={{
                        marginTop: 'auto', justifyContent: 'center',
                        background: 'linear-gradient(135deg, #00d4aa, #00b894)',
                        color: '#020617', fontSize: 12, padding: '8px 16px',
                      }}
                    >
                      Use This Topic →
                    </button>
                  </div>
                );
              })}
            </div>
          )}

          {/* Empty */}
          {!topicsTabLoading && topicsTabData.length === 0 && (
            <div style={{ textAlign: 'center', padding: '60px 0', color: c.textSecondary }}>
              <div style={{ fontSize: 48, marginBottom: 16 }}>✦</div>
              <p style={{ fontSize: 15 }}>Click "Suggest Topics" to generate AI-powered blog ideas.</p>
            </div>
          )}
        </div>
      )}

      {/* ══════════════════════════════
         DELETE CONFIRMATION MODAL
      ══════════════════════════════ */}
      {deleteConfirm && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 999,
          background: c.modalOverlay,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: 24,
        }}
          onClick={() => setDeleteConfirm(null)}
        >
          <div
            className="modal-content"
            style={{
              background: c.modalBg, border: `1px solid ${c.border}`,
              borderRadius: 16, padding: 32, maxWidth: 420, width: '100%',
              boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
            }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ fontSize: 40, textAlign: 'center', marginBottom: 16 }}>🗑️</div>
            <h3 style={{ fontSize: 18, fontWeight: 700, color: c.textPrimary, textAlign: 'center', margin: '0 0 10px' }}>
              Delete Post?
            </h3>
            <p style={{ color: c.textSecondary, fontSize: 14, textAlign: 'center', margin: '0 0 24px' }}>
              This action is permanent and cannot be undone.
            </p>
            <div style={{ display: 'flex', gap: 10 }}>
              <button
                className="ba-btn"
                onClick={() => setDeleteConfirm(null)}
                style={{ flex: 1, justifyContent: 'center', background: 'transparent', color: c.textPrimary, border: `1px solid ${c.border}`, padding: '12px' }}
              >
                Cancel
              </button>
              <button
                className="ba-btn"
                disabled={actionLoading === deleteConfirm + '_del'}
                onClick={() => handleDelete(deleteConfirm)}
                style={{ flex: 1, justifyContent: 'center', background: '#ef4444', color: '#fff', padding: '12px' }}
              >
                {actionLoading === deleteConfirm + '_del' ? 'Deleting...' : 'Yes, Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ══════════════════════════════
         AI TOPICS MODAL (from editor)
      ══════════════════════════════ */}
      {aiTopicsModal && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 999,
          background: c.modalOverlay,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: 24,
        }}
          onClick={() => setAiTopicsModal(false)}
        >
          <div
            className="modal-content"
            style={{
              background: c.modalBg, border: `1px solid ${c.border}`,
              borderRadius: 16, padding: 28, maxWidth: 640, width: '100%',
              maxHeight: '80vh', display: 'flex', flexDirection: 'column',
              boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
            }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
              <h3 style={{ fontSize: 17, fontWeight: 700, color: c.textPrimary, margin: 0 }}>AI Topic Suggestions</h3>
              <button onClick={() => setAiTopicsModal(false)} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: c.textSecondary }}>×</button>
            </div>

            <div style={{ overflowY: 'auto', flex: 1 }}>
              {aiTopicsLoading ? (
                <div style={{ textAlign: 'center', padding: '40px 0' }}>
                  <div style={{ width: 32, height: 32, border: '3px solid #1e293b', borderTopColor: '#00d4aa', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 12px' }} />
                  <p style={{ color: c.accent, fontSize: 13 }}>Generating topic ideas<LoadingDots /></p>
                </div>
              ) : aiTopics.length === 0 ? (
                <p style={{ color: c.textSecondary, textAlign: 'center', padding: '40px 0' }}>No topics returned. Try again.</p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {aiTopics.map((topic, i) => {
                    const catColor = CATEGORY_COLORS[topic.category] || '#00d4aa';
                    return (
                      <div
                        key={i}
                        style={{
                          padding: '14px 16px', borderRadius: 12,
                          border: `1px solid ${c.border}`, background: c.cardBg2,
                          cursor: 'pointer', transition: 'all 0.2s',
                          display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12,
                        }}
                        onMouseEnter={e => e.currentTarget.style.borderColor = c.accent}
                        onMouseLeave={e => e.currentTarget.style.borderColor = c.border}
                      >
                        <div style={{ flex: 1 }}>
                          {topic.category && (
                            <span style={{
                              fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px',
                              color: catColor, display: 'block', marginBottom: 4,
                            }}>
                              {topic.category}
                            </span>
                          )}
                          <p style={{ fontWeight: 600, color: c.textPrimary, fontSize: 14, margin: '0 0 4px' }}>{topic.title}</p>
                          {topic.description && (
                            <p style={{ color: c.textSecondary, fontSize: 12, margin: 0, lineHeight: 1.5 }}>{topic.description}</p>
                          )}
                        </div>
                        <button
                          className="ba-btn"
                          onClick={() => {
                            setForm(f => ({ ...f, title: topic.title || '', category: topic.category || f.category }));
                            setAiTopicsModal(false);
                          }}
                          style={{ background: c.accent, color: '#020617', padding: '7px 14px', fontSize: 12, flexShrink: 0 }}
                        >
                          Use
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BlogAdmin;
